import { makeAutoObservable } from 'mobx'
import { fetchTasks } from 'api/task'
import { studentsStore } from 'pages/Students/model/studentsStore'
import { buildTaskFreshnessMeta, buildTaskStudentKey } from 'pages/Classroom/utils/taskFreshness'
import { resolveTaskType } from 'pages/Classroom/utils/taskTypeResolver'

const DEFAULT_FILTERS = {
	status: [], // ['active', 'completed']
}

class ClassroomStore {
	assignments = []
	rawTasks = []
	searchQuery = ''
	tasksLoadStatus = 'idle'

	// Скелетон KPI только при первой загрузке (реальный запрос), не при повторном рендере/возврате
	tasksFetchedOnce = false
	lastTasksSyncAt = null
	freshnessMetaByKey = new Map()
	assignmentBadgeKeys = new Set()
	pendingIsNewByKey = new Map()

	activeFilterIds = ['all'] // filters bar: 'all' | 'active' | 'completed' | 'low'
	assignmentTypeFilter = null
	sortKey = null // 'name' | 'status' | 'students' | 'completion' | 'created' | 'lastUpdated'
	sortDir = 'asc' // 'asc' | 'desc'

	filters = { ...DEFAULT_FILTERS }
	hasFilters = false

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true })
	}

	setTasksFetchedOnce(value) {
		this.tasksFetchedOnce = value
	}

	setTasksLoadStatus(value) {
		this.tasksLoadStatus = value || 'idle'
	}

	normalizeTask(task) {
		const rawTitle = task?.task || task?.task_title
		const resolvedType = resolveTaskType({
			type: task?.type,
			title: rawTitle,
		})
		return {
			...task,
			type: resolvedType,
			task: rawTitle || 'Untitled Assignment',
			task_title: rawTitle || 'Untitled Assignment',
		}
	}

	ingestTasks(tasks) {
		const list = Array.isArray(tasks) ? tasks : []
		const normalizedTasks = list.map(task => this.normalizeTask(task))
		const assignmentsMap = new Map()
		const nextFreshness = new Map(this.freshnessMetaByKey)
		const nextBadgeKeys = new Set(this.assignmentBadgeKeys)

		normalizedTasks.forEach(task => {
			const taskId = String(task.task_id || '').trim()
			const studentId = String(task.studentId || '').trim()
			if (!taskId || !studentId) return

			const studentKey = buildTaskStudentKey({ taskId, studentId })
			const prevMeta = nextFreshness.get(studentKey) || null
			const nextMeta = {
				...(prevMeta || buildTaskFreshnessMeta(task)),
				taskLastUpdatedAt:
					buildTaskFreshnessMeta(task).taskLastUpdatedAt ||
					prevMeta?.taskLastUpdatedAt ||
					null,
				lastKnownStatus: String(task.student_status || '').trim() || null,
			}
			if (
				prevMeta?.lastKnownStatus &&
				prevMeta.lastKnownStatus !== 'completed' &&
				nextMeta.lastKnownStatus === 'completed'
			) {
				nextBadgeKeys.add(studentKey)
			}
			nextFreshness.set(studentKey, nextMeta)

			if (!assignmentsMap.has(taskId)) {
				const createdAt = task.created_at ? new Date(task.created_at) : null
				const created = createdAt
					? createdAt.toLocaleDateString('en-US', {
							month: 'short',
							day: 'numeric',
							year: 'numeric',
						})
					: null
				const isDocumentType =
					String(task.type || '').toLowerCase() === 'document'
				const name =
					isDocumentType && (!task.task || task.task === 'Form Completion')
						? 'Documents'
						: task.task || 'Untitled Assignment'
				assignmentsMap.set(taskId, {
					id: taskId,
					name,
					type: task.type || 'General',
					status: 'active',
					studentsCount: 0,
					completion: 0,
					_completedCount: 0,
					newStudentsCount: 0,
					lastUpdated: null,
					_lastUpdatedTs: null,
					created,
					createdAt: task.created_at || null,
					meta: '',
					assignmentData: {
						title: name,
						description: task.task_description || '',
						custom_message: task.custom_message || '',
						due_date: task.dueDate || task.due_date,
						start_date: task.start_date,
						end_date: task.end_date,
						type: task.type || 'General',
						assignment_url: task.assignment_url,
					},
					students: [],
				})
			}

			const assignment = assignmentsMap.get(taskId)
			let studentGrade = task.grade || null
			if (!studentGrade) {
				const fullStudentData = studentsStore.getStudentById(task.studentId)
				studentGrade = fullStudentData?.grade || null
			}
			const studentStatus =
				task.student_status || (task.response_text ? 'completed' : 'not_started')
			const normalizedStatus =
				studentStatus === 'completed'
					? 'completed'
					: studentStatus === 'in_progress'
						? 'in_progress'
						: 'not_started'
			const pendingKey = buildTaskStudentKey({ taskId, studentId })
			const isNew = Boolean(this.pendingIsNewByKey.get(pendingKey))
			assignment.students.push({
				id: task.studentId,
				name: task.studentName || 'Unknown Student',
				country: task.studentCountry,
				grade: studentGrade,
				status: normalizedStatus,
				response_text: task.response_text,
				submission_date: task.submission_date,
				completed_at: task.completed_at,
				taskId,
				isNew,
			})
			if (isNew) assignment.newStudentsCount += 1
			assignment.studentsCount++
			if (normalizedStatus === 'completed') assignment._completedCount++
			const candidateTs = Math.max(
				task.completed_at ? new Date(task.completed_at).getTime() || 0 : 0,
				task.submission_date ? new Date(task.submission_date).getTime() || 0 : 0,
			)
			if (
				candidateTs &&
				(!assignment._lastUpdatedTs || candidateTs > assignment._lastUpdatedTs)
			) {
				assignment._lastUpdatedTs = candidateTs
			}
		})

		const nextAssignments = Array.from(assignmentsMap.values())
		nextAssignments.forEach(assignment => {
			assignment.completion =
				assignment.studentsCount > 0
					? Math.round((assignment._completedCount / assignment.studentsCount) * 100)
					: 0
			assignment.status =
				assignment.completion === 100
					? 'completed'
					: assignment.completion > 0
						? 'in_progress'
						: 'active'
			if (assignment._lastUpdatedTs) {
				const d = new Date(assignment._lastUpdatedTs)
				assignment.lastUpdatedAt = d.toISOString()
				assignment.lastUpdated = d.toLocaleDateString('en-US', {
					month: 'short',
					day: 'numeric',
					year: 'numeric',
				})
			}
			delete assignment._completedCount
			delete assignment._lastUpdatedTs
		})

		this.rawTasks = normalizedTasks
		this.assignments = nextAssignments
		this.freshnessMetaByKey = nextFreshness
		this.assignmentBadgeKeys = nextBadgeKeys
		this.lastTasksSyncAt = new Date().toISOString()
	}

	async refreshTasks() {
		this.setTasksLoadStatus('loading')
		try {
			const response = await fetchTasks()
			const tasks = response?.tasks || []
			this.ingestTasks(tasks)
			this.setTasksLoadStatus('success')
			this.setTasksFetchedOnce(true)
			return tasks
		} catch (e) {
			this.setTasksLoadStatus('error')
			this.setTasksFetchedOnce(true)
			throw e
		}
	}

	markAssignmentsSeen() {
		this.assignmentBadgeKeys = new Set()
	}

	setPendingIsNewFromTasks(tasks) {
		const next = new Map()
		const list = Array.isArray(tasks) ? tasks : []
		list.forEach(task => {
			if (!task?.is_new) return
			const taskId = String(task.task_id || '').trim()
			const studentId = String(task.studentId || '').trim()
			if (!taskId || !studentId) return
			next.set(buildTaskStudentKey({ taskId, studentId }), true)
		})
		this.pendingIsNewByKey = next
		this.reapplyPendingNewFlags()
	}

	clearPendingForAssignment(assignmentId) {
		const id = String(assignmentId || '').trim()
		if (!id) return

		let changed = false
		for (const key of [...this.pendingIsNewByKey.keys()]) {
			if (key.startsWith(`${id}:`)) {
				this.pendingIsNewByKey.delete(key)
				changed = true
			}
		}
		if (changed) {
			this.reapplyPendingNewFlags()
		}
	}

	reapplyPendingNewFlags() {
		this.assignments = this.assignments.map(assignment => {
			let newStudentsCount = 0
			const students = (assignment.students || []).map(student => {
				const key = buildTaskStudentKey({
					taskId: assignment.id,
					studentId: student.id,
				})
				const isNew = Boolean(this.pendingIsNewByKey.get(key))
				if (isNew) newStudentsCount += 1
				return { ...student, isNew }
			})
			return { ...assignment, students, newStudentsCount }
		})
	}

	markTaskDetailsLoaded({ taskId, studentId, type }) {
		const key = buildTaskStudentKey({ taskId, studentId })
		if (!key || key === ':') return
		const prev = this.freshnessMetaByKey.get(key) || {}
		const nowIso = new Date().toISOString()
		const next = {
			...prev,
			...(type === 'career_quiz'
				? { quizLastLoadedAt: nowIso }
				: { profileLastLoadedAt: nowIso }),
		}
		this.freshnessMetaByKey.set(key, next)
	}

	getTaskFreshnessMeta({ taskId, studentId }) {
		const key = buildTaskStudentKey({ taskId, studentId })
		return this.freshnessMetaByKey.get(key) || null
	}

	setAssignments(list) {
		this.assignments = Array.isArray(list) ? list : []
	}

	addAssignment(assignment) {
		if (!assignment || assignment.id == null) return
		const id = String(assignment.id)
		const exists = this.assignments.some(a => String(a.id) === id)
		if (exists) return
		this.assignments = [assignment, ...this.assignments]
	}

	updateAssignment(id, patch) {
		if (id == null) return
		const key = String(id)
		this.assignments = this.assignments.map(a => {
			if (String(a.id) !== key) return a
			return { ...a, ...(patch || {}) }
		})
	}

	removeAssignment(id) {
		if (id == null) return
		const key = String(id)
		this.assignments = this.assignments.filter(a => String(a.id) !== key)
	}

	clearAssignments() {
		this.assignments = []
	}

	setSearchQuery(query) {
		this.searchQuery = query || ''
	}

	setSort(key, dir) {
		if (!key) return

		if (dir === 'asc' || dir === 'desc') {
			this.sortKey = key
			this.sortDir = dir
			return
		}

		if (this.sortKey === key) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
		} else {
			this.sortKey = key
			this.sortDir = 'asc'
		}
	}

	clearSort() {
		this.sortKey = null
		this.sortDir = 'asc'
	}

	setActiveFilterIds(ids) {
		const list = Array.isArray(ids) ? ids.map(String) : []
		this.activeFilterIds = list.length ? list : ['all']
	}

	setAssignmentTypeFilter(type) {
		const normalized = String(type || '').trim()
		this.assignmentTypeFilter = normalized || null
	}

	clearAssignmentTypeFilter() {
		this.assignmentTypeFilter = null
	}

	resetFiltersToAll() {
		this.activeFilterIds = ['all']
		this.filters = { ...DEFAULT_FILTERS }
		this.hasFilters = false
	}

	applyFilters(filters) {
		this.filters = { ...DEFAULT_FILTERS, ...(filters || {}) }

		const hasAny = (this.filters.status?.length || 0) > 0
		this.hasFilters = hasAny
	}

	clearFilters() {
		this.filters = { ...DEFAULT_FILTERS }
		this.hasFilters = false
	}

	get allAssignments() {
		return [...this.assignments]
	}

	// Keep backward compatibility
	get allActicities() {
		return [...this.assignments]
	}

	get filteredAssignments() {
		let list = [...this.assignments]

		if (this.searchQuery) {
			const q = this.searchQuery.toLowerCase()
			list = list.filter(a =>
				String(a.name || '')
					.toLowerCase()
					.includes(q)
			)
		}

		if (this.assignmentTypeFilter) {
			const targetType = String(this.assignmentTypeFilter).trim().toLowerCase()
			list = list.filter(
				a => String(a.type || '').trim().toLowerCase() === targetType,
			)
		}

		const active = (this.activeFilterIds || []).filter(x => x !== 'all')
		if (active.length) {
			const set = new Set(active)

			list = list.filter(a => {
				if (set.has('active') && String(a.status) === 'active') return true
				if (set.has('completed') && String(a.status) === 'completed')
					return true

				if (set.has('low')) {
					const c = Number(a.completion)
					if (!Number.isNaN(c) && c < 60 && String(a.status) === 'active')
						return true
				}

				if (set.has('new') && (Number(a.newStudentsCount) || 0) > 0) {
					return true
				}

				return false
			})
		}

		if (this.hasFilters) {
			const f = this.filters
			if (f.status?.length) {
				list = list.filter(a => f.status.includes(String(a.status)))
			}
		}

		if (this.sortKey) {
			const dir = this.sortDir === 'asc' ? 1 : -1
			const key = this.sortKey

			const getValue = a => {
				if (key === 'name') return a.name
				if (key === 'status') return a.status
				if (key === 'students') return a.studentsCount
				if (key === 'completion') return a.completion
				if (key === 'created') return a.createdAt ? new Date(a.createdAt).getTime() : null
				if (key === 'lastUpdated') return a.lastUpdated
				return a[key]
			}

			list.sort((a, b) => {
				const av = getValue(a)
				const bv = getValue(b)

				if (av === null && bv === null) return 0
				if (av === null) return 1
				if (bv === null) return -1

				if (typeof av === 'number' && typeof bv === 'number') {
					return (av - bv) * dir
				}

				return (
					String(av).localeCompare(String(bv), undefined, {
						sensitivity: 'base',
					}) * dir
				)
			})
		}

		return list
	}

	// Keep backward compatibility
	get filteredActivities() {
		return this.filteredAssignments
	}

	get assignmentBadgeText() {
		const count = this.assignmentBadgeKeys.size
		if (!count) return ''
		return count > 9 ? '9+' : String(count)
	}
}

export const classroomStore = new ClassroomStore()
